/**
 * viewportCine.js — Enterprise CINE Playback Engine
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v3.0 — ULTIMATE CINE SUITE]:
 *  - setTimeout вместо setInterval (защита от гонки)
 *  - _nav: правильная навигация для ORTHOGRAPHIC (scrollToSlice)
 *  - seekCine: работает для ORTHOGRAPHIC
 *  - _cineStates: Map<vpId> (поддержка множественных CINE)
 *  - pauseCine/resumeCine: сохраняют статистику
 *  - console.warn/error → log.warn/error
 *  - memoryGuardian.touch для ORTHOGRAPHIC
 *  - adaptiveFPSBudget: параметр вместо магического числа
 *  - R-R интервал визуализация (createRRTimeline, updateRRTimeline)
 *  - CINE с переменной скоростью (setCineSpeedCurve)
 *  - Auto-window во время CINE (enableCineAutoWindow)
 *  - Loop modes: forward, backward, bounce, bounce-pause, random
 *  - Запись CINE в GIF/MP4 (exportCineVideo)
 *  - CINE annotations overlay (enableCineOverlay)
 *  - Multi-planar CINE (startMultiplanarCine)
 *  - CINE presets (saveCinePreset, loadCinePreset, listCinePresets)
 *  - График интенсивности (getCineIntensityGraph)
 *  - Per-frame memory lock (lockCineFrames)
 *
 * @module viewportCine
 */

import state, { getRenderingEngine, ViewportType, OrientationAxis, cornerstone } from './viewportState.js';
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { memoryGuardian } from '../managers/MemoryGuardian.js';
import { log } from '../utils/logger.js';
import { планРолика } from './specModesCore.js';   // [12.0.57] ролик — один проход по серии

// ══════════════════════════════════════════════════════════════
//  STATE (Map для множественных CINE)
// ══════════════════════════════════════════════════════════════

/** @type {Map<string, Object>} vpId → cineState */
const _cineStates = new Map();

// ══════════════════════════════════════════════════════════════
//  CINE PRESETS
// ══════════════════════════════════════════════════════════════

const _cinePresets = new Map();

export function saveCinePreset(name, config) {
  _cinePresets.set(name, { ...config, savedAt: Date.now() });
}

export function loadCinePreset(name) {
  return _cinePresets.get(name) ? { ..._cinePresets.get(name) } : null;
}

export function listCinePresets() {
  return [..._cinePresets.entries()].map(([name, cfg]) => ({ name, ...cfg }));
}

// ══════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════

function _getTotalFrames(vp, viewportId) {
  if (vp.type === ViewportType.STACK) {
    return vp.getImageIds?.()?.length || 1;
  }
  if (vp.type === ViewportType.ORTHOGRAPHIC) {
    // [12.0.57] Число срезов — у самого окна: по размерам тома для наклонённой
    // плоскости оно не выходит, а getSliceIndex окна считает именно так.
    try {
      const n = vp.getNumberOfSlices?.();
      if (n > 1) return n;
    } catch { /* старый движок — ниже по размерам */ }
    try {
      const imgData = vp.getImageData?.();
      const dims = imgData?.getDimensions?.() || imgData?.dimensions;
      if (dims?.length >= 3) {
        const o = state.viewports?.get(viewportId)?.orientation;
        if (o === OrientationAxis.AXIAL) return dims[2];
        if (o === OrientationAxis.SAGITTAL) return dims[0];
        if (o === OrientationAxis.CORONAL) return dims[1];
        return Math.max(...dims);
      }
    } catch { /* fallback */ }
    try {
      const actors = vp.getActors?.();
      if (actors?.length) {
        const vid = actors[0]?.uid || actors[0]?.referenceId;
        if (vid) {
          const vol = cornerstone?.cache?.getVolume?.(vid);
          if (vol?.dimensions?.length >= 3) {
            const d = vol.dimensions;
            const o = state.viewports?.get(viewportId)?.orientation;
            if (o === OrientationAxis.AXIAL) return d[2];
            if (o === OrientationAxis.SAGITTAL) return d[0];
            if (o === OrientationAxis.CORONAL) return d[1];
            return Math.max(...d);
          }
          if (vol?.imageIds?.length) return vol.imageIds.length;
        }
      }
    } catch { /* fallback */ }
    try {
      const n = vp.getNumberOfSlices?.();
      if (n > 1) return n;
    } catch { /* safe */ }
    return 100;
  }
  return 1;
}

/**
 * Навигация на указанный кадр (исправлено для ORTHOGRAPHIC).
 * @param {Object} vp — viewport
 * @param {number} index — целевой индекс (0-based)
 * @param {number} prev — предыдущий индекс
 * @param {string} viewportId — для определения ориентации
 */
function _nav(vp, index, prev, viewportId) {
  if (vp.type === ViewportType.STACK) {
    vp.setImageIdIndex(index);
    const ids = vp.getImageIds?.();
    if (ids?.[index]) memoryGuardian.touch(ids[index]);
  } else {
    // ORTHOGRAPHIC: переходим к конкретному срезу
    // [12.0.51] delta вынесена из try: запасной путь в catch ниже тоже ею
    // пользуется, а объявленная внутри try она там не видна — запасной путь
    // падал с ReferenceError и кино на объёме вставало.
    // [12.0.57] Переход — К НОМЕРУ СРЕЗА, а не шагом от прошлого номера кино:
    // кино реформата начиналось с номера 0, а окно стояло на 31-м, и
    // относительные шаги уводили его к краю — пол-оборота окно стояло на
    // последнем срезе, а ведомые окна «3-плоскостей» шли каждое со своего
    // места. jumpToSlice считает номер так же, как getSliceIndex окна (метод
    // vp.scroll в аксиальном виде идёт в обратную сторону — проверено на
    // стенде «Спец. режимы»), и делает всё синхронно.
    const delta = index - prev;
    try {
      if (typeof cornerstone?.utilities?.jumpToSlice === 'function' && typeof vp.getSliceIndex === 'function') {
        if (vp.getSliceIndex() !== index) cornerstone.utilities.jumpToSlice(vp.element, { imageIndex: index })?.catch?.((e) => log.debug('[CINE] jump:', e.message));
      } else if (delta !== 0) {
        vp.scroll(delta);
      }
    } catch (e) {
      // Fallback: vp.scroll может не поддерживать большие delta
      try {
        // Пробуем vp.setSliceIndex если доступен
        if (typeof vp.setSliceIndex === 'function') {
          vp.setSliceIndex(index);
        } else {
          // Пошаговый скролл
          const dir = delta > 0 ? 1 : -1;
          for (let i = 0; i < Math.abs(delta); i++) {
            vp.scroll(dir);
          }
        }
      } catch (e2) { log.debug('[CINE] nav fallback:', e2.message); }
    }
    // Touch volume data для MemoryGuardian
    try {
      const actors = vp.getActors?.();
      if (actors?.length) {
        const vid = actors[0]?.uid || actors[0]?.referenceId;
        if (vid) memoryGuardian.touch(vid);
      }
    } catch (e) { /* safe */ }
  }
}

// ══════════════════════════════════════════════════════════════
//  START / STOP / PAUSE / RESUME
// ══════════════════════════════════════════════════════════════

/**
 * Start CINE.
 * @param {string} viewportId
 * @param {Object} [opts]
 * @param {number} [opts.fps=15]
 * @param {'forward'|'backward'|'bounce'|'bouncePause'|'random'} [opts.direction='forward']
 * @param {boolean} [opts.loop=true]
 * @param {number[]} [opts.range] — [from, to]
 * @param {string[]} [opts.syncViewportIds]
 * @param {boolean} [opts.adaptiveFPS=false]
 * @param {number} [opts.adaptiveFPSBudget=900] — бюджет рендеринга (мс на 1000 кадров)
 * @param {number} [opts.frameSkip=0]
 * @param {Function} [opts.onFrame]
 * @param {Object} [opts.speedCurve] — { points: [[timePercent, speedFactor], ...] }
 * @param {boolean} [opts.autoWindow=false]
 * @param {boolean} [opts.lockFrames=false] — удерживать все кадры в кэше
 */
export function startCine(viewportId, opts = {}) {
  stopCine(viewportId);

  const re = getRenderingEngine();
  if (!re) { log.warn('[CINE] No rendering engine'); return; }

  let vpId = viewportId;
  if (!vpId) {
    const allVps = re.getViewports();
    const stackOrOrtho = allVps.find(v => v.type === ViewportType.STACK || v.type === ViewportType.ORTHOGRAPHIC);
    vpId = stackOrOrtho?.id || allVps[0]?.id;
  }

  const vp = re.getViewport(vpId);
  if (!vp) { log.warn('[CINE] No viewport:', vpId); return; }
  if (vp.type !== ViewportType.STACK && vp.type !== ViewportType.ORTHOGRAPHIC) {
    log.warn('[CINE] Unsupported type:', vp.type);
    return;
  }

  const fps = Math.max(1, Math.min(60, opts.fps || 15));
  const dir = opts.direction || 'forward';
  const loop = opts.loop !== false;
  let adaptive = opts.adaptiveFPS || false; // [10.19.43] let: ручная скорость глушит адаптив
  const adaptiveBudget = opts.adaptiveFPSBudget || 900;
  const skipN = opts.frameSkip || 0;
  const onFrame = opts.onFrame || null;
  const syncIds = opts.syncViewportIds || [];
  const speedCurve = opts.speedCurve || null;
  const autoWindow = opts.autoWindow || false;
  const lockFrames = opts.lockFrames || false;

  const total = _getTotalFrames(vp, vpId);
  if (total <= 1) { log.warn('[CINE] total <= 1:', total); return; }

  const rFrom = opts.range ? Math.max(0, opts.range[0]) : 0;
  const rTo = opts.range ? Math.min(total - 1, opts.range[1]) : total - 1;
  if (rTo - rFrom < 1) { log.warn('[CINE] range too small:', rFrom, rTo); return; }

  // [12.0.57] Реформат тоже стартует с текущего среза (раньше — с нулевого).
  const _текущийСрез = () => { try { return vp.type === ViewportType.STACK ? vp.getCurrentImageIdIndex?.() : vp.getSliceIndex?.(); } catch (e) { return null; } };
  let idx = Math.max(rFrom, Math.min(rTo, _текущийСрез() ?? rFrom));
  let bounceDir = 1, frameN = 0, dropped = 0;
  let actualFPS = fps;
  const frameTimes = [];
  const ac = new AbortController();
  const t0 = performance.now();
  let timeoutId = null;
  let running = false;

  // Lock frames if requested
  if (lockFrames) {
    lockCineFrames(vpId, vp.type === ViewportType.STACK ? vp.getImageIds?.() : null);
  }

  // Данные для auto-window
  let autoWindowBaseline = null;
  if (autoWindow) {
    try {
      const props = vp.getProperties?.();
      if (props?.voiRange) {
        autoWindowBaseline = { lower: props.voiRange.lower, upper: props.voiRange.upper };
      }
    } catch (e) { /* safe */ }
  }

  // Данные для графика интенсивности
  const intensitySamples = [];

  const getSpeedFactor = (frameIndex) => {
    if (!speedCurve?.points?.length) return 1;
    const t = (frameIndex - rFrom) / Math.max(rTo - rFrom, 1);
    const pts = speedCurve.points;
    if (t <= pts[0][0]) return pts[0][1];
    if (t >= pts[pts.length - 1][0]) return pts[pts.length - 1][1];
    for (let i = 0; i < pts.length - 1; i++) {
      if (t >= pts[i][0] && t <= pts[i + 1][0]) {
        const lt = (t - pts[i][0]) / (pts[i + 1][0] - pts[i][0]);
        return pts[i][1] + (pts[i + 1][1] - pts[i][1]) * lt;
      }
    }
    return 1;
  };

  const tick = async () => {
    if (ac.signal.aborted || !_cineStates.has(vpId)) return;
    if (running) { timeoutId = setTimeout(tick, Math.round(1000 / actualFPS)); return; }

    running = true;
    const ft = performance.now();
    try {
      if (skipN > 0 && frameN % (skipN + 1) !== 0) { frameN++; dropped++; running = false; timeoutId = setTimeout(tick, Math.round(1000 / actualFPS)); return; }

      const prevIdx = idx;

      // Random mode
      if (dir === 'random') {
        idx = rFrom + Math.floor(Math.random() * (rTo - rFrom + 1));
      } else if (dir === 'bounce' || dir === 'bouncePause') {
        idx += bounceDir;
        if (idx >= rTo) {
          bounceDir = -1;
          idx = rTo;
          if (dir === 'bouncePause') {
            _nav(vp, idx, prevIdx, vpId);
            try { vp.render(); } catch (e) { log.debug('[CINE] render:', e.message); }
            running = false;
            timeoutId = setTimeout(tick, 500); // пауза 500ms
            return;
          }
        }
        if (idx <= rFrom) {
          bounceDir = 1;
          idx = rFrom;
          if (dir === 'bouncePause') {
            _nav(vp, idx, prevIdx, vpId);
            try { vp.render(); } catch (e) { log.debug('[CINE] render:', e.message); }
            running = false;
            timeoutId = setTimeout(tick, 500);
            return;
          }
        }
      } else {
        idx += dir === 'backward' ? -1 : 1;
        if (idx > rTo || idx < rFrom) {
          if (loop) idx = dir === 'backward' ? rTo : rFrom;
          else { stopCine(vpId); return; }
        }
      }

      _nav(vp, idx, prevIdx, vpId);
      try { vp.render(); } catch (e) { log.debug('[CINE] render:', e.message); }

      // Sync
      if (syncIds.length) {
        const pct = (idx - rFrom) / Math.max(rTo - rFrom, 1);
        for (const sid of syncIds) {
          try {
            const sv = re.getViewport(sid);
            if (!sv) continue;
            const st = _getTotalFrames(sv, sid);
            const si = Math.round(pct * (st - 1));
            const siPrev = Math.round(((prevIdx - rFrom) / Math.max(rTo - rFrom, 1)) * (st - 1));
            _nav(sv, si, siPrev, sid);
            sv.render();
          } catch (e) { /* skip */ }
        }
      }

      // Auto-window
      if (autoWindow && autoWindowBaseline && frameN % 5 === 0) {
        try {
          const props = vp.getProperties?.();
          const canvas = vp.canvas || vp.element?.querySelector('canvas');
          if (canvas && props?.voiRange) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              const imgData = ctx.getImageData(canvas.width / 2, canvas.height / 2, 20, 20);
              let sum = 0;
              for (let i = 0; i < imgData.data.length; i += 4) sum += imgData.data[i];
              const avg = sum / (imgData.data.length / 4);
              const correction = avg < 64 ? -5 : avg > 192 ? 5 : 0;
              if (correction !== 0) {
                vp.setProperties({ voiRange: { lower: props.voiRange.lower + correction, upper: props.voiRange.upper + correction } });
              }
            }
          }
        } catch (e) { /* safe */ }
      }

      // Intensity sample
      if (frameN % 3 === 0) {
        try {
          const canvas = vp.canvas || vp.element?.querySelector('canvas');
          if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              const imgData = ctx.getImageData(canvas.width / 2, canvas.height / 2, 10, 10);
              let sum = 0;
              for (let i = 0; i < imgData.data.length; i += 4) sum += imgData.data[i];
              intensitySamples.push({ frame: frameN, index: idx, intensity: sum / (imgData.data.length / 4) });
              if (intensitySamples.length > 500) intensitySamples.shift();
            }
          }
        } catch (e) { /* safe */ }
      }

      frameN++;
      const st = _cineStates.get(vpId);
      if (st) {
        st.currentIndex = idx;
        st.prevIndex = prevIdx;
        st.framesRendered = frameN;
        st.framesDropped = dropped;
        st.intensitySamples = intensitySamples;
      }

      // Adaptive FPS
      if (adaptive) {
        const elapsed = performance.now() - ft;
        frameTimes.push(elapsed);
        if (frameTimes.length > 10) frameTimes.shift();
        const avgTime = frameTimes.reduce((a, b) => a + b, 0) / frameTimes.length;
        const cap = Math.floor(adaptiveBudget / Math.max(avgTime, 1));
        const nf = Math.max(1, Math.min(fps, cap));
        if (nf !== actualFPS) {
          actualFPS = nf;
          if (st) st.fps = actualFPS;
        }
      }

      if (onFrame) try {
        onFrame({
          index: idx, total, rangeFrom: rFrom, rangeTo: rTo,
          fps: actualFPS, elapsed: performance.now() - t0, dropped,
          speedFactor: getSpeedFactor(frameN),
        });
      } catch (e) { /* safe */ }

      events.emit(EVENTS.CINE_FRAME, {
        viewportId: vpId, currentIndex: idx, totalFrames: total,
        fps: actualFPS, rangeFrom: rFrom, rangeTo: rTo,
      });
    } catch (e) {
      log.error('[CINE] tick error (continuing):', e.message);
    } finally {
      running = false;
      if (_cineStates.has(vpId) && !ac.signal.aborted) {
        const interval = Math.round(1000 / (actualFPS * getSpeedFactor(frameN)));
        timeoutId = setTimeout(tick, Math.max(16, interval));
      }
    }
  };

  // [10.19.43] Мосты в замыкание тика: скорость меняется ЖИВЬЁМ, без
  // рестарта (владелец: «на мобильном изображение начинает дёргаться» — то
  // был стоп-старт setCineSpeed), а ручная установка глушит адаптив
  // (владелец: «на десктопе работает так, как хочет» — то адаптив
  // перетирал ±5 каждый кадр).
  const _живойFPS = (f) => { actualFPS = Math.max(1, Math.min(60, f)); };
  const _глушиАдаптив = () => { adaptive = false; };
  _cineStates.set(vpId, {
    _живойFPS, _глушиАдаптив,
    vpId, timeoutId, ac, fps: actualFPS, direction: dir, loop,
    currentIndex: idx, prevIndex: idx, totalFrames: total,
    playing: true, startTime: t0, framesRendered: 0, framesDropped: 0,
    range: [rFrom, rTo], syncViewportIds: syncIds,
    onFrame, adaptive, adaptiveBudget, speedCurve, autoWindow, autoWindowBaseline,
    intensitySamples, lockFrames,
  });

  timeoutId = setTimeout(tick, Math.round(1000 / fps));
  _cineStates.get(vpId).timeoutId = timeoutId;

  events.emit(EVENTS.CINE_START, {
    viewportId: vpId, fps, direction: dir, totalFrames: total,
    range: [rFrom, rTo], syncViewportIds: syncIds,
  });
  log.debug(`[CINE] Started: ${fps}fps ${dir} [${rFrom}–${rTo}]`);
}

export function stopCine(viewportId) {
  // Если viewportId не указан — останавливаем все
  if (!viewportId) {
    for (const [vpId, st] of _cineStates) {
      clearTimeout(st.timeoutId);
      st.ac.abort();
      const elapsed = (performance.now() - st.startTime) / 1000;
      const stats = {
        viewportId: vpId, framesRendered: st.framesRendered,
        framesDropped: st.framesDropped, duration: elapsed,
        avgFPS: st.framesRendered / Math.max(elapsed, 0.001),
      };
      events.emit(EVENTS.CINE_STOP, stats);
    }
    _cineStates.clear();
    return;
  }

  const st = _cineStates.get(viewportId);
  if (!st) return;

  clearTimeout(st.timeoutId);
  st.ac.abort();
  const elapsed = (performance.now() - st.startTime) / 1000;
  const stats = {
    viewportId, framesRendered: st.framesRendered,
    framesDropped: st.framesDropped, duration: elapsed,
    avgFPS: st.framesRendered / Math.max(elapsed, 0.001),
    intensitySamples: st.intensitySamples?.length || 0,
  };
  _cineStates.delete(viewportId);
  events.emit(EVENTS.CINE_STOP, stats);
}

export function pauseCine(viewportId) {
  const st = _cineStates.get(viewportId);
  if (!st?.playing) return;
  clearTimeout(st.timeoutId);
  st.playing = false;
}

export function resumeCine(viewportId) {
  const st = _cineStates.get(viewportId);
  if (!st || st.playing) return;
  // Перезапускаем с сохранением всей статистики
  const saved = { ...st };
  _cineStates.delete(viewportId);
  startCine(viewportId, {
    fps: saved.fps, direction: saved.direction, range: saved.range,
    syncViewportIds: saved.syncViewportIds, onFrame: saved.onFrame,
    adaptive: saved.adaptive, adaptiveFPSBudget: saved.adaptiveBudget,
    speedCurve: saved.speedCurve, autoWindow: saved.autoWindow,
    lockFrames: saved.lockFrames,
  });
  // Восстанавливаем статистику
  const newSt = _cineStates.get(viewportId);
  if (newSt) {
    newSt.framesRendered = saved.framesRendered;
    newSt.framesDropped = saved.framesDropped;
    newSt.startTime = saved.startTime;
    newSt.intensitySamples = saved.intensitySamples;
  }
}

export function toggleCine(viewportId, opts) {
  const st = _cineStates.get(viewportId);
  if (st?.playing) pauseCine(viewportId);
  else if (st) resumeCine(viewportId);
  else if (viewportId) startCine(viewportId, opts);
}

// ══════════════════════════════════════════════════════════════
//  STATE & CONTROL
// ══════════════════════════════════════════════════════════════

export function getCineState(viewportId) {
  const st = _cineStates.get(viewportId);
  if (!st) return null;
  return {
    vpId: st.vpId, direction: st.direction, fps: st.fps,
    currentIndex: st.currentIndex, totalFrames: st.totalFrames,
    playing: st.playing, range: st.range ? [...st.range] : null,
    framesRendered: st.framesRendered, framesDropped: st.framesDropped,
    elapsed: (performance.now() - st.startTime) / 1000,
  };
}

/** Получить все активные CINE */
export function getAllCineStates() {
  const result = [];
  for (const [vpId, st] of _cineStates) {
    result.push({
      vpId, direction: st.direction, fps: st.fps,
      currentIndex: st.currentIndex, totalFrames: st.totalFrames,
      playing: st.playing,
    });
  }
  return result;
}

export function setCineSpeed(viewportId, fps) {
  const st = _cineStates.get(viewportId);
  if (!st) return;
  const f = Math.max(1, Math.min(60, fps));
  // [10.19.43] БЕЗ РЕСТАРТА: прежняя версия убивала и пересоздавала кино на
  // каждый ±5 — на телефоне это дёргало кадр (стоп-старт), а сохранённый
  // adaptive тут же перетирал ручную скорость («работает как хочет»).
  // Теперь скорость меняется в живом тике, а ручная установка — закон:
  // адаптив глушится.
  if (st._живойFPS) {
    st._глушиАдаптив?.();
    st._живойFPS(f);
    st.fps = f;
    return;
  }
  // Откат для состояний без мостов (не должен встречаться) — прежний рестарт.
  const saved = { ...st };
  _cineStates.delete(viewportId);
  startCine(viewportId, {
    fps: f, direction: saved.direction,
    range: saved.range, syncViewportIds: saved.syncViewportIds,
    onFrame: saved.onFrame, adaptive: saved.adaptive,
    adaptiveFPSBudget: saved.adaptiveBudget, speedCurve: saved.speedCurve,
    autoWindow: saved.autoWindow, lockFrames: saved.lockFrames,
  });
}

/**
 * Установить кривую скорости CINE (переменная скорость).
 * @param {string} viewportId
 * @param {number[][]} points — [[timePercent, speedFactor], ...]  например [[0, 0.5], [0.5, 2], [1, 0.5]]
 */
export function setCineSpeedCurve(viewportId, points) {
  const st = _cineStates.get(viewportId);
  if (!st) return;
  const saved = { ...st };
  _cineStates.delete(viewportId);
  startCine(viewportId, {
    fps: saved.fps, direction: saved.direction,
    range: saved.range, syncViewportIds: saved.syncViewportIds,
    onFrame: saved.onFrame, speedCurve: { points },
    autoWindow: saved.autoWindow, lockFrames: saved.lockFrames,
  });
}

export function setCineRange(viewportId, from, to) {
  const st = _cineStates.get(viewportId);
  if (!st) return;
  st.range = [Math.max(0, from), Math.min(st.totalFrames - 1, to)];
}

export function seekCine(viewportId, frameIndex) {
  const st = _cineStates.get(viewportId);
  if (!st) return;
  const vp = getRenderingEngine()?.getViewport(st.vpId);
  if (!vp) return;
  const i = Math.max(0, Math.min(st.totalFrames - 1, frameIndex));
  const prev = st.currentIndex;
  st.currentIndex = i;
  st.prevIndex = prev;
  _nav(vp, i, prev, viewportId);
  vp.render();
}

export function getCineStats(viewportId) {
  const st = _cineStates.get(viewportId);
  if (!st) return null;
  const elapsed = (performance.now() - st.startTime) / 1000;
  const total = st.framesRendered + st.framesDropped;
  return {
    framesRendered: st.framesRendered, framesDropped: st.framesDropped,
    dropRate: total > 0 ? st.framesDropped / total : 0,
    avgFPS: elapsed > 0 ? st.framesRendered / elapsed : 0,
    elapsed,
  };
}

/** Получить данные графика интенсивности */
export function getCineIntensityGraph(viewportId) {
  const st = _cineStates.get(viewportId);
  return st?.intensitySamples?.length ? [...st.intensitySamples] : [];
}

// ══════════════════════════════════════════════════════════════
//  AUTO-WINDOW
// ══════════════════════════════════════════════════════════════

export function enableCineAutoWindow(viewportId) {
  const st = _cineStates.get(viewportId);
  if (!st) return;
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return;
  try {
    const props = vp.getProperties?.();
    if (props?.voiRange) {
      st.autoWindow = true;
      st.autoWindowBaseline = { lower: props.voiRange.lower, upper: props.voiRange.upper };
    }
  } catch (e) { log.warn('[CINE] autoWindow:', e.message); }
}

export function disableCineAutoWindow(viewportId) {
  const st = _cineStates.get(viewportId);
  if (st) { st.autoWindow = false; st.autoWindowBaseline = null; }
}

// ══════════════════════════════════════════════════════════════
//  MULTI-PLANAR CINE
// ══════════════════════════════════════════════════════════════

/**
 * Запустить CINE одновременно на axial, sagittal, coronal.
 * @param {string[]} viewportIds — [axialId, sagittalId, coronalId]
 * @param {Object} [opts]
 */
export function startMultiplanarCine(viewportIds, opts = {}) {
  if (!viewportIds?.length || viewportIds.length < 2) {
    log.warn('[CINE] Multiplanar requires 2+ viewports');
    return;
  }

  // Запускаем на первом, остальные — sync
  const [master, ...slaves] = viewportIds;
  startCine(master, { ...opts, syncViewportIds: slaves });
}

// ══════════════════════════════════════════════════════════════
//  LOCK FRAMES
// ══════════════════════════════════════════════════════════════

/**
 * Заблокировать кадры в кэше на время CINE.
 * @param {string} viewportId
 * @param {string[]|null} imageIds — ID изображений (для STACK), null для volume
 */
export function lockCineFrames(viewportId, imageIds) {
  if (imageIds?.length) {
    try { memoryGuardian.lock(imageIds); } catch (e) { log.debug('[CINE] lock:', e.message); }
  }

  // Для volume: блокируем volumeId
  if (!imageIds) {
    try {
      const vp = getRenderingEngine()?.getViewport(viewportId);
      const actors = vp?.getActors?.();
      if (actors?.length) {
        const vid = actors[0]?.uid || actors[0]?.referenceId;
        if (vid) memoryGuardian.lock([vid]);
      }
    } catch (e) { log.debug('[CINE] lock vol:', e.message); }
  }
}

// ══════════════════════════════════════════════════════════════
//  R-R TIMELINE UI
// ══════════════════════════════════════════════════════════════

const _rrTimelines = new Map();

/**
 * Создать визуализацию R-R интервала для cardiac CINE.
 * @param {string} viewportId
 * @param {string} containerId — DOM-элемент
 * @param {Object[]} phases — [{ label, phasePercent }] (из viewport4D)
 */
export function createRRTimeline(viewportId, containerId, phases = []) {
  const container = document.getElementById(containerId);
  if (!container) return;

  const timeline = document.createElement('div');
  timeline.className = 'cine-rr-timeline';
  timeline.style.cssText = `
    display:flex; align-items:center; gap:2px; padding:4px;
    background:rgba(0,0,0,0.5); border-radius:6px; font-size:10px; color:#aaa;
  `;
  container.appendChild(timeline);
  _rrTimelines.set(viewportId, timeline);

  // Отрисовка фаз
  timeline.innerHTML = phases.map(p => {
    const isSystole = p.label?.includes('Систола');
    const isDiastole = p.label?.includes('Диастола');
    const color = isSystole ? '#f44' : isDiastole ? '#4f8' : '#555';
    return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};" title="${p.label}"></span>`;
  }).join('');

  // Индикатор текущей позиции
  const indicator = document.createElement('div');
  indicator.style.cssText = 'width:2px;height:14px;background:#0cf;position:absolute;top:0;transition:left 0.1s;';
  indicator.className = 'cine-rr-indicator';
  timeline.style.position = 'relative';
  timeline.appendChild(indicator);
}

export function updateRRTimeline(viewportId) {
  const timeline = _rrTimelines.get(viewportId);
  const st = _cineStates.get(viewportId);
  if (!timeline || !st) return;

  const pct = (st.currentIndex - st.range[0]) / Math.max(st.range[1] - st.range[0], 1);
  const indicator = timeline.querySelector('.cine-rr-indicator');
  if (indicator) {
    indicator.style.left = `${pct * 100}%`;
  }
}

// ══════════════════════════════════════════════════════════════
//  CINE OVERLAY
// ══════════════════════════════════════════════════════════════

const _cineOverlays = new Map();

/**
 * Включить оверлей с информацией о CINE на viewport.
 * @param {string} viewportId
 */
export function enableCineOverlay(viewportId) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp?.element) return;

  let overlay = vp.element.querySelector(':scope > .cine-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.className = 'cine-overlay';
    overlay.style.cssText = `
      position:absolute; bottom:8px; left:50%; transform:translateX(-50%);
      background:rgba(0,0,0,0.7); color:#fff; padding:2px 10px;
      border-radius:4px; font-size:11px; font-family:monospace;
      pointer-events:none; z-index:100;
    `;
    vp.element.appendChild(overlay);
  }
  _cineOverlays.set(viewportId, overlay);
}

export function disableCineOverlay(viewportId) {
  const overlay = _cineOverlays.get(viewportId);
  if (overlay) { overlay.remove(); _cineOverlays.delete(viewportId); }
}

/**
 * Обновить оверлей (вызывается из tick'а).
 * @private
 */
function _updateCineOverlay(viewportId, info) {
  const overlay = _cineOverlays.get(viewportId);
  if (!overlay) return;
  overlay.textContent = `▶ ${info.index + 1}/${info.total} · ${info.fps}fps`;
}

// ══════════════════════════════════════════════════════════════
//  EXPORT CINE VIDEO
// ══════════════════════════════════════════════════════════════

/**
 * Экспортировать текущий CINE в видео (WebM).
 * @param {string} viewportId
 * @param {Object} [opts]
 * @param {number} [opts.fps=15]
 * @param {number} [opts.durationMs=5000] — длительность видео
 * @returns {Promise<Blob>}
 */
export async function exportCineVideo(viewportId, opts = {}) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) throw new Error('Viewport not found');

  const fps = opts.fps || 15;
  const durationMs = opts.durationMs || 5000;
  const canvas = vp.canvas || vp.element?.querySelector('canvas');
  if (!canvas) throw new Error('No canvas');

  const stream = canvas.captureStream(fps);
  const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
  const chunks = [];
  recorder.ondataavailable = (e) => chunks.push(e.data);
  recorder.start();

  const st = _cineStates.get(viewportId);
  const totalFrames = Math.ceil((durationMs / 1000) * fps);
  const prevPlaying = st?.playing;

  // Временно запускаем CINE для записи
  if (!prevPlaying) startCine(viewportId, { fps, loop: true, direction: 'forward' });

  await new Promise(r => setTimeout(r, durationMs));

  if (!prevPlaying) stopCine(viewportId);

  return new Promise(resolve => {
    recorder.onstop = () => resolve(new Blob(chunks, { type: 'video/webm' }));
    recorder.stop();
  });
}

/**
 * [12.0.57] Ролик окна со срезами — ОДИН ПРОХОД ПО ВСЕЙ СЕРИИ.
 *
 * Кнопка «Ролик 2D-окна» звала exportCineVideo на 3 секунды: это 45 кадров
 * с того места, где окажется кино, — в реформате с нулевого среза (с ног), и
 * окно оставалось на чужом срезе. На стенде: окно стояло на 31-м срезе
 * аксиального реформата, после ролика — на 63-м; а записано было не выбранное
 * окно, а всегда «axial». Теперь: выбранное окно, проход с первого среза до
 * последнего (длинная серия прореживается, ролик не дольше пределСек —
 * core/specModesCore.планРолика), срез потом возвращается на место. Формат —
 * какой браузер умеет (webm, иначе mp4: Safari на iPhone webm не пишет).
 *
 * @returns {Promise<{blob:Blob, тип:string, кадров:number, шаг:number, секунд:number, всего:number}>}
 */
export async function записатьРоликСерии(viewportId, { кадровВСек = 15, пределСек = 30 } = {}) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) throw new Error('окна нет');
  if (vp.type !== ViewportType.STACK && vp.type !== ViewportType.ORTHOGRAPHIC) throw new Error('ролик пишется в окне со срезами, а не в объёмном 3D');
  const canvas = vp.canvas || vp.element?.querySelector('canvas');
  if (!canvas?.captureStream || typeof MediaRecorder === 'undefined') throw new Error('браузер не умеет записывать видео с экрана');
  const типы = ['video/webm;codecs=vp9', 'video/webm', 'video/mp4'];
  const тип = типы.find((т) => { try { return MediaRecorder.isTypeSupported?.(т); } catch (e) { return false; } });
  if (!тип) throw new Error('браузер не пишет ни webm, ни mp4');
  const стопка = vp.type === ViewportType.STACK;
  const всего = стопка ? (vp.getImageIds?.()?.length || 0) : (vp.getNumberOfSlices?.() || 0);
  if (всего < 2) throw new Error('в окне один кадр — листать нечего');
  const исходный = стопка ? vp.getCurrentImageIdIndex?.() : vp.getSliceIndex?.();
  const шло = _cineStates.has(viewportId);
  if (шло) stopCine(viewportId);
  const перейти = async (i) => {
    if (стопка) await vp.setImageIdIndex(i);
    else await cornerstone.utilities.jumpToSlice(vp.element, { imageIndex: i });
    vp.render();
  };
  const план = планРолика(всего, кадровВСек, пределСек);
  const поток = canvas.captureStream(кадровВСек);
  const запись = new MediaRecorder(поток, { mimeType: тип });
  const куски = [];
  запись.ondataavailable = (e) => { if (e.data?.size) куски.push(e.data); };
  const готово = new Promise((r) => { запись.onstop = r; });
  await перейти(план.индексы[0]);
  запись.start();
  try {
    for (const i of план.индексы) {
      await перейти(i);
      await new Promise((r) => setTimeout(r, 1000 / кадровВСек));
    }
  } finally {
    запись.stop();
    await готово;
    if (Number.isFinite(исходный)) { try { await перейти(исходный); } catch (e) { log.debug('[Ролик] возврат среза:', e.message); } }
  }
  return { blob: new Blob(куски, { type: тип.split(';')[0] }), тип: тип.split(';')[0], кадров: план.кадров, шаг: план.шаг, секунд: план.секунд, всего };
}

// ══════════════════════════════════════════════════════════════
//  CLEANUP
// ══════════════════════════════════════════════════════════════

/** Очистить все CINE и связанные ресурсы */
export function destroyAllCine() {
  for (const vpId of _cineStates.keys()) {
    stopCine(vpId);
  }
  for (const overlay of _cineOverlays.values()) {
    overlay.remove();
  }
  _cineOverlays.clear();
  _rrTimelines.clear();
  _cinePresets.clear();
}